import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

type Props = {
  isTrial: boolean;
  isExpired: boolean;
};

const SESSION_KEY = 'trial_popup_dismissed';

/**
 * Pop-up #101 affichée 1 fois par session aux users en essai gratuit ou expirés,
 * pour les inviter à s'abonner. Dismiss = sessionStorage (revient à la prochaine session).
 */
export default function TrialPopup({ isTrial, isExpired }: Props) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!isTrial && !isExpired) return;
    if (sessionStorage.getItem(SESSION_KEY) === '1') return;
    setOpen(true);
  }, [isTrial, isExpired]);

  const dismiss = () => {
    sessionStorage.setItem(SESSION_KEY, '1');
    setOpen(false);
  };

  if (!open) return null;

  const isAr = i18n.language === 'ar';
  const title = isExpired ? t('trial.popupTitleExpired') : t('trial.popupTitleTrial');
  const body = isExpired ? t('trial.popupBodyExpired') : t('trial.popupBodyTrial');

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed', inset: 0, zIndex: 1050,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '16px',
      }}
      onClick={dismiss}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        dir={isAr ? 'rtl' : 'ltr'}
        style={{
          background: 'white', borderRadius: '8px', padding: '24px',
          maxWidth: '480px', width: '100%', textAlign: 'center',
          boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
        }}
      >
        <i className={isExpired ? 'bi bi-exclamation-circle-fill' : 'bi bi-stars'}
           style={{ fontSize: '48px', color: '#981934' }}></i>
        <h4 style={{ marginTop: '16px', color: '#981934' }}>{title}</h4>
        <p style={{ color: '#555', marginTop: '12px' }}>{body}</p>
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', marginTop: '20px' }}>
          <Link
            to={`/${i18n.language}/dashboard/subscription`}
            onClick={dismiss}
            className="btn btn-primary"
          >
            <i className="bi bi-credit-card me-2"></i>
            {t('trial.popupSubscribeNow')}
          </Link>
          <button type="button" onClick={dismiss} className="btn btn-outline-secondary">
            {t('trial.popupLater')}
          </button>
        </div>
      </div>
    </div>
  );
}
